import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CertificationGallery = () => {
  const [selectedCertification, setSelectedCertification] = useState(null);
  const [filter, setFilter] = useState('all');

  const certifications = [
    {
      id: 1,
      title: 'AWS Solutions Architect Professional',
      issuer: 'Amazon Web Services',
      date: '2023-08-15',
      category: 'cloud',
      level: 'Professional',
      credentialId: 'AWS-SAP-2023-8472',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&h=300&fit=crop',
      description: 'Advanced certification demonstrating expertise in designing distributed applications and systems on the AWS platform.',
      skills: ['Cloud Architecture', 'AWS Services', 'Security', 'Cost Optimization'],
      verificationUrl: 'https://aws.amazon.com/verification',
      color: '#ff6b35'
    },
    {
      id: 2,
      title: 'Google Cloud Professional Developer',
      issuer: 'Google Cloud',
      date: '2023-06-22',
      category: 'cloud',
      level: 'Professional',
      credentialId: 'GCP-PD-2023-9156',
      image: 'https://images.unsplash.com/photo-1573804633927-bfcbcd909acd?w=400&h=300&fit=crop',
      description: 'Certification validating skills in developing scalable and highly available applications using Google Cloud technologies.',
      skills: ['GCP Services', 'Kubernetes', 'Cloud Functions', 'BigQuery'],
      verificationUrl: 'https://cloud.google.com/certification',
      color: '#00ff88'
    },
    {
      id: 3,
      title: 'React Advanced Patterns',
      issuer: 'Meta (Facebook)',
      date: '2023-04-10',
      category: 'frontend',
      level: 'Advanced',
      credentialId: 'META-RAP-2023-3847',
      image: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&h=300&fit=crop',
      description: 'Advanced React certification covering complex patterns, performance optimization, and modern React ecosystem.',
      skills: ['React Hooks', 'Context API', 'Performance', 'Testing'],
      verificationUrl: 'https://developers.facebook.com/certification',
      color: '#00d4ff'
    },
    {
      id: 4,
      title: 'Kubernetes Administrator (CKA)',
      issuer: 'Cloud Native Computing Foundation',
      date: '2023-03-18',
      category: 'devops',
      level: 'Professional',
      credentialId: 'CNCF-CKA-2023-7291',
      image: 'https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?w=400&h=300&fit=crop',
      description: 'Hands-on certification demonstrating skills in Kubernetes cluster administration and management.',
      skills: ['Cluster Management', 'Networking', 'Security', 'Troubleshooting'],
      verificationUrl: 'https://www.cncf.io/certification',
      color: '#8844ff'
    },
    {
      id: 5,
      title: 'MongoDB Certified Developer',
      issuer: 'MongoDB Inc.',
      date: '2023-02-05',
      category: 'database',
      level: 'Associate',
      credentialId: 'MDB-DEV-2023-5638',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=300&fit=crop',
      description: 'Certification validating proficiency in MongoDB database design, development, and optimization.',
      skills: ['NoSQL Design', 'Aggregation', 'Indexing', 'Performance Tuning'],
      verificationUrl: 'https://university.mongodb.com/certification',
      color: '#ffaa00'
    },
    {
      id: 6,
      title: 'Certified Ethical Hacker (CEH)',
      issuer: 'EC-Council',
      date: '2022-11-30',
      category: 'security',
      level: 'Professional',
      credentialId: 'ECC-CEH-2022-9472',
      image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=400&h=300&fit=crop',
      description: 'Comprehensive certification in ethical hacking methodologies and cybersecurity practices.',
      skills: ['Penetration Testing', 'Vulnerability Assessment', 'Security Auditing', 'Risk Management'],
      verificationUrl: 'https://www.eccouncil.org/verification',
      color: '#ff4444'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Certifications', icon: 'Award', color: '#00d4ff' },
    { id: 'cloud', name: 'Cloud Computing', icon: 'Cloud', color: '#00ff88' },
    { id: 'frontend', name: 'Frontend Development', icon: 'Monitor', color: '#8844ff' },
    { id: 'devops', name: 'DevOps & Infrastructure', icon: 'Server', color: '#ffaa00' },
    { id: 'database', name: 'Database Management', icon: 'Database', color: '#ff6b35' },
    { id: 'security', name: 'Cybersecurity', icon: 'Shield', color: '#ff4444' }
  ];

  const filteredCertifications = filter === 'all' 
    ? certifications 
    : certifications.filter(cert => cert.category === filter);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="w-full max-w-6xl mx-auto">
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="font-orbitron font-bold text-3xl text-foreground mb-4">
          Certification Gallery
        </h2>
        <p className="text-text-secondary font-inter max-w-2xl mx-auto">
          Professional credentials and certifications showcased in holographic frames with verification links
        </p>
      </motion.div>

      {/* Category Filters */}
      <div className="flex flex-wrap justify-center gap-3 mb-8">
        {categories.map((category, index) => (
          <motion.button
            key={category.id}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-inter font-medium text-sm transition-all duration-300 cyber-hover ${
              filter === category.id
                ? 'cyber-border-active cyber-glow text-foreground'
                : 'cyber-border text-text-secondary hover:text-foreground'
            }`}
            onClick={() => setFilter(category.id)}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Icon 
              name={category.icon} 
              size={16} 
              style={{ color: filter === category.id ? category.color : 'currentColor' }} 
            />
            <span>{category.name}</span>
            {filter === category.id && (
              <div className="w-2 h-2 rounded-full bg-brand-cyan animate-pulse"></div>
            )}
          </motion.button>
        ))}
      </div>

      {/* Certifications Grid */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        layout
      >
        <AnimatePresence>
          {filteredCertifications.map((cert, index) => (
            <motion.div
              key={cert.id}
              className="glass-morphism rounded-xl overflow-hidden cyber-border cursor-pointer cyber-hover group"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              onClick={() => setSelectedCertification(cert)}
              layout
            >
              {/* Certification Image */}
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={cert.image}
                  alt={cert.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                
                {/* Holographic Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-transparent via-brand-cyan/10 to-brand-purple/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Level Badge */}
                <div className="absolute top-3 right-3">
                  <div 
                    className="px-3 py-1 rounded-full text-xs font-mono font-bold text-background cyber-glow"
                    style={{ backgroundColor: cert.color }}
                  >
                    {cert.level}
                  </div>
                </div>

                {/* Verification Status */}
                <div className="absolute top-3 left-3">
                  <div className="flex items-center space-x-1 bg-background/80 backdrop-blur-sm rounded-full px-2 py-1">
                    <div className="w-2 h-2 bg-brand-green rounded-full animate-pulse"></div>
                    <span className="text-xs font-mono text-brand-green">VERIFIED</span>
                  </div>
                </div>
              </div>

              {/* Certification Details */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-inter font-bold text-foreground text-lg leading-tight mb-1">
                      {cert.title}
                    </h3>
                    <p className="text-text-secondary text-sm">
                      {cert.issuer}
                    </p>
                  </div>
                  <Icon 
                    name="ExternalLink" 
                    size={16} 
                    className="text-text-tertiary group-hover:text-brand-cyan transition-colors duration-300" 
                  />
                </div>

                <div className="flex items-center space-x-4 text-xs text-text-tertiary font-mono mb-4">
                  <div className="flex items-center space-x-1">
                    <Icon name="Calendar" size={12} />
                    <span>{formatDate(cert.date)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Hash" size={12} />
                    <span>{cert.credentialId.slice(-4)}</span>
                  </div>
                </div>

                {/* Skills Tags */}
                <div className="flex flex-wrap gap-2">
                  {cert.skills.slice(0, 3).map((skill, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-surface/50 rounded text-xs text-text-secondary font-mono"
                    >
                      {skill}
                    </span>
                  ))}
                  {cert.skills.length > 3 && (
                    <span className="px-2 py-1 bg-surface/50 rounded text-xs text-text-secondary font-mono">
                      +{cert.skills.length - 3}
                    </span>
                  )}
                </div>
              </div>

              {/* Holographic Frame */}
              <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-brand-cyan/50 transition-all duration-300 pointer-events-none">
                <div className="absolute -top-1 -left-1 w-4 h-4 border-t-2 border-l-2 border-brand-cyan opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute -top-1 -right-1 w-4 h-4 border-t-2 border-r-2 border-brand-cyan opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute -bottom-1 -left-1 w-4 h-4 border-b-2 border-l-2 border-brand-cyan opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 border-b-2 border-r-2 border-brand-cyan opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {/* Certification Details Modal */}
      <AnimatePresence>
        {selectedCertification && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedCertification(null)}
          >
            <motion.div
              className="glass-morphism rounded-2xl p-8 max-w-2xl w-full cyber-border-active cyber-glow-intense max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div
                    className="w-16 h-16 rounded-xl flex items-center justify-center cyber-glow-intense"
                    style={{ backgroundColor: `${selectedCertification.color}20` }}
                  >
                    <Icon name="Award" size={32} style={{ color: selectedCertification.color }} />
                  </div>
                  <div>
                    <h3 className="font-orbitron font-bold text-2xl text-foreground">
                      {selectedCertification.title}
                    </h3>
                    <p className="text-text-secondary">
                      {selectedCertification.issuer}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedCertification(null)}
                  className="p-2 rounded-lg hover:bg-surface/50 transition-colors duration-200"
                >
                  <Icon name="X" size={24} className="text-text-secondary" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="font-inter font-semibold text-foreground mb-3">Description</h4>
                  <p className="text-text-secondary leading-relaxed">
                    {selectedCertification.description}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-inter font-semibold text-foreground mb-3">Certification Details</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-text-secondary">Issue Date:</span>
                        <span className="text-foreground font-mono">
                          {formatDate(selectedCertification.date)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-secondary">Level:</span>
                        <span 
                          className="font-mono font-bold"
                          style={{ color: selectedCertification.color }}
                        >
                          {selectedCertification.level}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-secondary">Credential ID:</span>
                        <span className="text-foreground font-mono">
                          {selectedCertification.credentialId}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-inter font-semibold text-foreground mb-3">Skills Covered</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedCertification.skills.map((skill, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 rounded-lg text-sm font-mono border"
                          style={{ 
                            backgroundColor: `${selectedCertification.color}10`,
                            borderColor: `${selectedCertification.color}40`,
                            color: selectedCertification.color
                          }}
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <button
                    className="w-full flex items-center justify-center space-x-2 py-3 rounded-lg font-inter font-medium transition-all duration-300 cyber-border hover:cyber-border-active hover:cyber-glow"
                    style={{ color: selectedCertification.color }}
                  >
                    <Icon name="ExternalLink" size={16} />
                    <span>Verify Certification</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gallery Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
        <motion.div
          className="glass-morphism rounded-xl p-6 cyber-border text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="w-12 h-12 bg-brand-cyan/20 rounded-lg flex items-center justify-center mx-auto mb-3 cyber-glow">
            <Icon name="Award" size={24} className="text-brand-cyan" />
          </div>
          <div className="font-orbitron font-bold text-2xl text-foreground mb-1">
            {certifications.length}
          </div>
          <div className="text-sm text-text-secondary">
            Active Certifications
          </div>
        </motion.div>

        <motion.div
          className="glass-morphism rounded-xl p-6 cyber-border text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="w-12 h-12 bg-brand-green/20 rounded-lg flex items-center justify-center mx-auto mb-3 cyber-glow">
            <Icon name="CheckCircle" size={24} className="text-brand-green" />
          </div>
          <div className="font-orbitron font-bold text-2xl text-foreground mb-1">
            100%
          </div>
          <div className="text-sm text-text-secondary">
            Verification Rate
          </div>
        </motion.div>

        <motion.div
          className="glass-morphism rounded-xl p-6 cyber-border text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="w-12 h-12 bg-brand-purple/20 rounded-lg flex items-center justify-center mx-auto mb-3 cyber-glow">
            <Icon name="TrendingUp" size={24} className="text-brand-purple" />
          </div>
          <div className="font-orbitron font-bold text-2xl text-foreground mb-1">
            {new Date().getFullYear()}
          </div>
          <div className="text-sm text-text-secondary">
            Latest Certification
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CertificationGallery;